import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Sprout, Youtube, Instagram, Linkedin, Facebook } from "lucide-react";

// Custom X (formerly Twitter) icon - lucide's Twitter is the old bird mark
const XIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const Footer = () => {
  const { t } = useLang();

  return (
    <footer data-testid="site-footer" className="bg-[#14361E] text-white/85 mt-8">
      <div className="container-gc px-4 sm:px-6 py-12 sm:py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" data-testid="footer-brand" className="inline-flex items-center gap-2.5">
              <div
                className="w-10 h-10 rounded-xl bg-white border border-white/20 flex-shrink-0"
                style={{
                  backgroundImage: "url('https://customer-assets-m6fa6gv7.emergentagent.net/job_site-framework-8/artifacts/u4lqn226_1780408844558.png')",
                  backgroundSize: "170% auto",
                  backgroundPosition: "center 22%",
                  backgroundRepeat: "no-repeat",
                }}
                aria-label="Garlic Capital"
              />
              <div className="leading-tight">
                <div className="font-bold text-white">{t.brand}</div>
                <div className="text-[11px] text-white/60 -mt-0.5">{t.brand_sub}</div>
              </div>
            </Link>
            <p className="mt-4 text-sm text-white/70 leading-relaxed max-w-xs">{t.footer.tagline}</p>
            <div className="mt-5 flex items-center gap-2 flex-wrap">
              {[
                { icon: XIcon, testid: "footer-x", href: "#", label: "X (Twitter)" },
                { icon: Facebook, testid: "footer-facebook", href: "#", label: "Facebook" },
                { icon: Instagram, testid: "footer-instagram", href: "#", label: "Instagram" },
                { icon: Youtube, testid: "footer-youtube", href: "#", label: "YouTube" },
                { icon: Linkedin, testid: "footer-linkedin", href: "#", label: "LinkedIn" },
              ].map((s, i) => {
                const Icon = s.icon;
                return (
                  <a
                    key={i}
                    href={s.href}
                    aria-label={s.label}
                    data-testid={s.testid}
                    className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 border border-white/10 flex items-center justify-center transition-colors"
                  >
                    <Icon size={16} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Explore */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-white/60 mb-4">{t.footer.explore}</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/" data-testid="footer-nav-home" className="hover:text-[#F59E0B] transition-colors">{t.nav.home}</Link></li>
              <li><Link to="/mandi" data-testid="footer-nav-mandi" className="hover:text-[#F59E0B] transition-colors">{t.nav.mandi}</Link></li>
              <li><Link to="/trade" data-testid="footer-nav-trade" className="hover:text-[#F59E0B] transition-colors">{t.nav.trade}</Link></li>
              <li><Link to="/analytics" data-testid="footer-nav-analytics" className="hover:text-[#F59E0B] transition-colors">{t.nav.analytics}</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-white/60 mb-4">{t.footer.company}</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/about" data-testid="footer-nav-about" className="hover:text-[#F59E0B] transition-colors">{t.nav.about}</Link></li>
              <li><Link to="/contact" data-testid="footer-nav-contact" className="hover:text-[#F59E0B] transition-colors">{t.nav.contact}</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-white/60 mb-4">{t.footer.legal}</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/terms" data-testid="footer-nav-terms" className="hover:text-[#F59E0B] transition-colors">{t.nav.terms}</Link></li>
              <li><Link to="/privacy" data-testid="footer-nav-privacy" className="hover:text-[#F59E0B] transition-colors">{t.nav.privacy}</Link></li>
              <li><Link to="/legal" data-testid="footer-nav-legal" className="hover:text-[#F59E0B] transition-colors">{t.nav.legal_info}</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-white/60">
          <div>© 2026 {t.brand} · {t.footer.rights}</div>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-bottom-terms">{t.nav.terms}</Link>
            <span className="text-white/20">·</span>
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-bottom-privacy">{t.nav.privacy}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
